import { useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import styles from './SlideOver.module.css';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { useFocusTrap } from '../hooks/useFocusTrap';

export type SlideOverWidth = 'default' | 'wide';

export interface SlideOverProps {
  open: boolean;
  onClose: () => void;
  title: string;
  width?: SlideOverWidth;
  footer?: ReactNode;
  children: ReactNode;
}

const WIDTH_CLASS: Record<SlideOverWidth, string> = {
  default: styles.widthDefault ?? '',
  wide: styles.widthWide ?? '',
};

export function SlideOver({
  open,
  onClose,
  title,
  width = 'default',
  footer,
  children,
}: SlideOverProps) {
  const titleId = useId();
  useEscapeKey(open, onClose);
  const panelRef = useFocusTrap<HTMLDivElement>(open);

  if (!open) return null;

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`${styles.panel} ${WIDTH_CLASS[width]}`}
      >
        <header className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close panel"
          >
            <X size={18} strokeWidth={1.5} aria-hidden="true" />
          </button>
        </header>
        <div className={styles.content}>{children}</div>
        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </>,
    document.body,
  );
}
